import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type CandidateStage =
  | 'applied' | 'screen' | 'interview' | 'offer' | 'hired' | 'rejected' | 'withdrawn';

export interface Candidate {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  roleTitle: string;
  countryCode: string | null;
  source: string | null;
  stage: CandidateStage;
  expectedSalaryCents: number | null;
  expectedCurrency: string | null;
  resumeUrl: string | null;
  notes: string | null;
  rejectedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CandidateEvent {
  id: string;
  kind: string;
  fromStage: CandidateStage | null;
  toStage: CandidateStage | null;
  body: string | null;
  userId: string | null;
  createdAt: string;
}

export type StageSummary = Record<CandidateStage, number>;

export function useCandidates(stage?: CandidateStage) {
  return useQuery({
    queryKey: ['candidates', stage ?? 'all'],
    queryFn: async () => {
      const r = await api.get<{ candidates: Candidate[] }>(
        `/api/hiring/candidates${stage ? `?stage=${stage}` : ''}`,
      );
      return r.data.candidates;
    },
  });
}

export function useCandidate(id: string | null) {
  return useQuery({
    queryKey: ['candidate', id],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get<{ candidate: Candidate; events: CandidateEvent[] }>(
        `/api/hiring/candidates/${id}`,
      );
      return r.data;
    },
  });
}

export function useStageSummary() {
  return useQuery({
    queryKey: ['candidates-summary'],
    queryFn: async () => {
      const r = await api.get<StageSummary>('/api/hiring/candidates/summary');
      return r.data;
    },
  });
}

export interface CreateCandidateArgs {
  fullName: string;
  email: string;
  phone?: string;
  roleTitle: string;
  countryCode?: string;
  source?: string;
  expectedSalaryCents?: number;
  expectedCurrency?: string;
  resumeUrl?: string;
  notes?: string;
}

export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateCandidateArgs) => {
      const r = await api.post<Candidate>('/api/hiring/candidates', args);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['candidates-summary'] });
    },
  });
}

export function useTransitionCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string; toStage: CandidateStage; rejectedReason?: string; note?: string;
    }) => {
      const r = await api.post<Candidate>(`/api/hiring/candidates/${args.id}/transition`, {
        toStage: args.toStage, rejectedReason: args.rejectedReason, note: args.note,
      });
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['candidate', vars.id] });
      qc.invalidateQueries({ queryKey: ['candidates-summary'] });
    },
  });
}

export interface HireArgs {
  id: string;
  employmentType:
    | 'fte' | 'contractor' | 'part_time' | 'intern'
    | 'seishain' | 'keiyaku' | 'gyoumu_itaku';
  hireDate: string;
  countryCode?: string;
  jobTitle?: string;
  baseSalaryCents?: number;
  salaryCurrency?: string;
  salaryPeriod?: 'monthly' | 'annual' | 'hourly';
}

export function useHireCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: HireArgs) => {
      const { id, ...body } = args;
      const r = await api.post<{ candidate: Candidate; employeeId: string }>(
        `/api/hiring/candidates/${id}/hire`, body,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['candidates-summary'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}
